<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="far fa-calendar-check" class="q-mb-sm" /> {{ $t('appointments') }}
		</div>

		<filters
			class="filter-card"
			:options="getOptionsFilter"
			@requestFiltered="requestFiltered"
			@clearAndRequest="clearAndRequest"
		/>

		<q-table
			:data="appointments"
			:columns="columns"
			:pagination.sync="server_pagination"
			@request="requestAppointments"
			:filter="filter"
			:loading="loading"
			row-key="id"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			class="q-mt-md"
			binary-state-sort
		>
			<template slot="top" slot-scope="props" :props="props">
				<q-search
					v-model="filter"
					:debounce="600"
					icon="fas fa-search"
					:placeholder="$t('search')"
				/>

				<div class="col" />

				<!-- <q-btn-dropdown color="secondary" :label="$t('reports_bi')" class="q-mr-sm" ref="dropdownReport" v-if="report_list.length > 0 && permissions.reports">
					<q-list highlight separator link>
						<q-item
							v-for="(report, index) in report_list"
							:key="`i${index}`"
							@click.native="showReportBI(report)">
							<q-item-main>
								{{ $t(report.slug) }}
							</q-item-main>
						</q-item>
					</q-list>
				</q-btn-dropdown> -->

				<q-btn
					color="secondary"
					v-if="permissions.reports"
					class="q-mr-sm"
					icon="fas fa-download"
					:disable="$buttonDelay('appointments_report_button_delay')"
					:label="$t('appointments_report')"
					@click="downloadReport()"
				>
					<q-tooltip
						v-if="$buttonDelay('appointments_report_button_delay')"
						anchor="top middle"
						self="bottom middle"
						:offset="[10, 10]"
					>
						{{ $t('report_button_delay') }}
					</q-tooltip>
				</q-btn>
				<q-btn
					color="secondary"
					icon="fas fa-plus"
					:label="$t('new')"
					@click="
						$router.push({
							name: 'appointment',
							params: { id: 0, permissions: permissions },
						})
					"
					:disable="!permissions.request_events"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props" v-show="col.show">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props">
				<q-td auto-width key="id" :props="props" v-show="showColumn(props.colsMap, 'id')">
					{{ props.row.id }}
				</q-td>

				<q-td key="title" :props="props" v-show="showColumn(props.colsMap, 'title')">
					{{ props.row.title }}
				</q-td>

				<q-td key="code" :props="props" v-show="showColumn(props.colsMap, 'code')">
					{{ props.row.code }}
				</q-td>

				<q-td key="tags" :props="props" v-show="showColumn(props.colsMap, 'tags')">
					<q-btn
						color="primary"
						text-color="white"
						:label="getQuantityTags(props.row.tags)"
						no-ripple
					>
						<q-tooltip v-if="props.row.tags.length > 0">
							<p class="tooltip q-mb-sm">
								{{ $t('tags') }}:
								<span v-for="(tag, index) in props.row.tags" :key="index">
									{{ getTag(tag) }}
									<span v-if="props.row.tags.length - 1 > index">, </span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>

				<q-td
					key="presential_event_appointment__appointment_type__title"
					:props="props"
					v-show="
						showColumn(
							props.colsMap,
							'presential_event_appointment__appointment_type__title'
						)
					"
				>
					{{ getAppointmentType(props.row.appointment_type_id) }}
				</q-td>

				<q-td
					key="user_requester_name_email"
					:props="props"
					v-show="showColumn(props.colsMap, 'user_requester_name_email')"
				>
					{{ props.row.user_requester_name_email }}
				</q-td>

				<q-td
					key="instructors_list"
					:props="props"
					v-show="showColumn(props.colsMap, 'instructors_list')"
				>
					<q-btn
						color="primary"
						text-color="white"
						:label="
							props.row.instructors_list.length
								? props.row.instructors_list.length
								: 0
						"
						no-ripple
					>
						<q-tooltip v-if="props.row.instructors_list.length">
							<p class="tooltip q-mb-sm">
								{{ $t('instructor') }}:
								<span
									v-for="(instructor, index) in props.row.instructors_list"
									:key="index"
								>
									{{ instructor }}
									<span v-if="props.row.instructors_list.length - 1 > index"
										>,
									</span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>

				<q-td
					key="company_branch_name"
					:props="props"
					v-show="showColumn(props.colsMap, 'company_branch_name')"
				>
					{{ props.row.company_branch_name }}
				</q-td>

				<q-td
					key="qty_vacancies"
					:props="props"
					v-show="showColumn(props.colsMap, 'qty_vacancies')"
				>
					{{ props.row.qty_vacancies }}
				</q-td>

				<q-td
					key="first_event_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'first_event_date')"
				>
					{{ props.row.first_event_date }}
				</q-td>

				<q-td
					key="last_event_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'last_event_date')"
				>
					{{ props.row.last_event_date }}
				</q-td>

				<q-td
					key="created_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'created_date')"
				>
					{{ props.row.created_date }}
				</q-td>

				<q-td
					key="updated_date"
					:props="props"
					v-show="showColumn(props.colsMap, 'updated_date')"
				>
					{{ props.row.updated_date }}
				</q-td>

				<q-td
					auto-width
					key="status"
					:props="props"
					v-show="showColumn(props.colsMap, 'status')"
				>
					<q-chip
						small
						:icon="getStatusIcon(props.row.status)"
						:color="getStatusColorChip(props.row.status)"
						class="full-width"
					>
						{{ $t(getStatus(props.row.status)) }}
					</q-chip>
				</q-td>

				<q-td
					auto-width
					key="actions"
					:props="props"
					v-show="showColumn(props.colsMap, 'actions')"
				>
					<q-btn
						flat
						round
						color="primary"
						icon="fas fa-pencil-alt"
						@click="
							$router.push({
								name: 'appointment',
								params: {
									id: props.row.actions,
									customTab: false,
									permissions: permissions,
								},
							})
						"
					>
						<q-tooltip>
							{{ $t('view_or_edit_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						v-if="showGuests(props.row)"
						flat
						round
						color="primary"
						icon="fas fa-list"
						@click="
							$router.push({
								name: 'appointment',
								params: {
									id: props.row.actions,
									status: props.row.status,
									customTab: true,
									autoDuplicate: false,
									permissions: permissions,
								},
							})
						"
					>
						<q-tooltip>
							{{ $t('access_subscription_list_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						v-if="props.row.status === 9"
						:disable="!permissions.request_events"
						flat
						round
						color="primary"
						icon="far fa-copy"
						@click="
							$router.push({
								name: 'appointment',
								params: {
									id: props.row.actions,
									status: props.row.status,
									customTab: false,
									autoDuplicate: true,
									permissions: permissions,
								},
							})
						"
					>
						<q-tooltip>
							{{ $t('duplicate_training_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						v-if="props.row.status === 8"
						flat
						round
						color="secondary"
						icon="fas fa-chart-line"
						@click="showPresentialReport(props.row)"
					>
						<q-tooltip>
							{{ $t('view_report_for_this_training_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>

			<q-tr slot="bottom-row" slot-scope="props" :props="props">
				<q-td colspan="100%">
					<q-btn
						flat
						round
						dense
						color="secondary"
						size="small"
						icon="fas fa-cog"
						@click="show_customize_columns = true"
					>
						<q-tooltip>
							{{ $t('configure_table_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</q-table>

		<q-modal
			v-model="show_customize_columns"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="saveColumns()" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('personalize_columns') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row gutter-md">
						<div
							class="col-xs-12 col-sm-4"
							v-for="(column, index) in columns"
							:key="index"
							:style="column.label === '#' ? 'display: none !important' : ''"
						>
							<q-checkbox
								v-model="column.show"
								:label="$t(column.label)"
								v-if="column.label && column.label != '#'"
							/>
						</div>
					</div>

					<div class="row reverse gutter-md q-mt-md">
						<div class="col-xs-12 col-sm-3">
							<q-btn
								icon="fas fa-times"
								color="primary"
								class="fit"
								:label="$t('close')"
								@click="saveColumns()"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-modal
			v-model="show_report"
			:content-css="{ minWidth: '100vw', maxWidth: '100vw', minHeight: '100vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_report = false" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('reports') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div style="position:relative; width:100%; height:0px; padding-bottom:56.25%;">
						<iframe
							style="position:absolute; left:0; top:0; width:100%; height:100%"
							:src="report_url"
							frameborder="0"
						>
						</iframe>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-modal
			v-model="show_report"
			:content-css="{ minWidth: '100vw', maxWidth: '100vw', minHeight: '100vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_report = false" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('reports') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div style="position:relative; width:100%; height:0px; padding-bottom:56.25%;">
						<iframe
							style="position:absolute; left:0; top:0; width:100%; height:100%"
							:src="report_url"
							frameborder="0"
						>
						</iframe>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-dialog v-model="show_report_problem" prevent-close>
			<span slot="title">{{ $t('report_download_failed') }}</span>

			<div slot="message">
				<span class="q-pa-xs">{{ $t('report_download_failed_exceeded_time') }}</span>
				<p class="q-pa-xs">{{ $t('the_report_it_will_be_sent_to_your_email') }}</p>
			</div>

			<template slot="buttons" slot-scope="props" :props="props" class="justify-center">
				<div class="row full-width q-mb-xs">
					<q-btn
						color="positive"
						class="offset-4 col-4"
						:label="$t('ok')"
						@click="show_report_problem = false"
					/>
				</div>
			</template>
		</q-dialog>

		<billing-reports
			v-if="show_billing_reports_modal"
			:show="show_billing_reports_modal"
			@hideBillingReports="hideBillingReports()"
		/>

		<reports
			v-if="show_report_presential"
			:show_report_presential="show_report_presential"
			:id="action_id"
			:status="action_status"
			:title="action_title"
			:permissions="permissions"
			@hide_report="hideReport()"
		/>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'
const { formatDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'
import Filters from 'components/admin/presential/Filters.vue'

export default {
	name: 'AdminPresentials',

	i18nOptions: { namespaces: ['custom', 'presential', 'common', 'admin', 'imports', 'users'] },

	components: {
		filters: Filters,
		'billing-reports': () => import('components/admin/presential/BillingReports.vue'),
		reports: () => import('components/admin/presential/Report.vue'),
	},

	data() {
		return {
			selected_status: [],
			appointments: [],
			action_id: null,
			action_status: null,
			action_title: null,
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'id',
					sortable: false,
					show: true,
				},
				{
					name: 'title',
					required: true,
					label: 'title',
					align: 'left',
					field: 'title',
					sortable: true,
					show: true,
				},
				{
					name: 'code',
					required: true,
					label: 'code',
					align: 'left',
					field: 'code',
					sortable: false,
					show: false,
				},
				{
					name: 'tags',
					required: true,
					label: 'tags',
					align: 'left',
					field: 'tags',
					sortable: false,
					show: true,
				},
				{
					name: 'presential_event_appointment__appointment_type__title',
					required: true,
					label: 'appointment_type',
					align: 'left',
					field: 'presential_event_appointment__appointment_type__title',
					sortable: true,
					show: true,
				},
				{
					name: 'user_requester_name_email',
					required: true,
					label: 'requester',
					align: 'left',
					field: 'user_requester_name_email',
					sortable: true,
					show: true,
				},
				{
					name: 'instructors_list',
					required: true,
					label: 'instructor',
					align: 'left',
					field: 'instructors_list',
					sortable: true,
					show: true,
				},
				{
					name: 'company_branch_name',
					required: true,
					label: 'location',
					align: 'left',
					field: 'company_branch_name',
					sortable: true,
					show: true,
				},
				{
					name: 'qty_vacancies',
					required: true,
					label: 'vacancies_left',
					align: 'left',
					field: 'qty_vacancies',
					sortable: true,
					show: false,
				},
				{
					name: 'first_event_date',
					required: true,
					label: 'start_date',
					align: 'left',
					field: 'first_event_date',
					sortable: false,
					show: true,
				},
				{
					name: 'last_event_date',
					required: true,
					label: 'end_date',
					align: 'left',
					field: 'last_event_date',
					sortable: false,
					show: true,
				},
				{
					name: 'created_date',
					required: true,
					label: 'created_date',
					align: 'left',
					field: 'created_date',
					sortable: false,
					show: true,
				},
				{
					name: 'updated_date',
					required: true,
					label: 'updated_date',
					align: 'left',
					field: 'updated_date',
					sortable: false,
					show: true,
				},
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'left',
					field: 'status',
					sortable: true,
					show: true,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'left',
					field: 'actions',
					sortable: false,
					show: true,
				},
			],
			columns_without_vacancies: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'id',
					sortable: false,
					show: true,
				},
				{
					name: 'title',
					required: true,
					label: 'title',
					align: 'left',
					field: 'title',
					sortable: true,
					show: true,
				},
				{
					name: 'tags',
					required: true,
					label: 'tags',
					align: 'left',
					field: 'tags',
					sortable: false,
					show: true,
				},
				{
					name: 'presential_event_appointment__appointment_type__title',
					required: true,
					label: 'appointment_type',
					align: 'left',
					field: 'presential_event_appointment__appointment_type__title',
					sortable: true,
					show: true,
				},
				{
					name: 'user_requester_name_email',
					required: true,
					label: 'requester',
					align: 'left',
					field: 'user_requester_name_email',
					sortable: true,
					show: true,
				},
				{
					name: 'instructors_list',
					required: true,
					label: 'instructor',
					align: 'left',
					field: 'instructors_list',
					sortable: true,
					show: true,
				},
				{
					name: 'company_branch_name',
					required: true,
					label: 'location',
					align: 'left',
					field: 'company_branch_name',
					sortable: true,
					show: true,
				},
				{
					name: 'first_event_date',
					required: true,
					label: 'start_date',
					align: 'left',
					field: 'first_event_date',
					sortable: false,
					show: true,
				},
				{
					name: 'last_event_date',
					required: true,
					label: 'end_date',
					align: 'left',
					field: 'last_event_date',
					sortable: false,
					show: true,
				},
				{
					name: 'created_date',
					required: true,
					label: 'created_date',
					align: 'left',
					field: 'created_date',
					sortable: false,
					show: true,
				},
				{
					name: 'updated_date',
					required: true,
					label: 'updated_date',
					align: 'left',
					field: 'updated_date',
					sortable: false,
					show: true,
				},
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'left',
					field: 'status',
					sortable: true,
					show: true,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'left',
					field: 'actions',
					sortable: false,
					show: true,
				},
			],
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: null,
				descending: false,
				sortBy: null,
			},
			first_load: false,
			show_filter: false,
			filter: '',
			filter_user_requester: [],
			filter_user_instructor: [],
			filter_company_branch: [],
			filter_appointment_type: [],
			filter_status: [],
			filter_start_date: [],
			filter_end_date: [],
			filter_event_start_date: [],
			filter_event_end_date: [],
			loading: false,
			dropdown_reports: false,
			show_report: false,
			show_billing_reports_modal: false,
			report_url: '',
			show_customize_columns: false,
			show_report_presential: false,
			show_report_problem: false,
			permissions: {
				approve_events: false,
				cancel_events: false,
				billing_reports: false,
				edit_tests: false,
				finish_events: false,
				manage_models: false,
				presential_edit_basic_data: false,
				presential_edit_evaluations: false,
				presential_edit_events: false,
				presential_full_view: false,
				presential_import_results: false,
				presential_print_results: false,
				presential_print_users: false,
				presential_save_results: false,
				reports: false,
				reports_panel: false,
				request_events: false,
				subscribe_event_users: false,
				trainings_manage: false,
				view_pre_scheduled_training: false,
			},
			show_report: false,
			report_list: [],
			report_url: '',
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			id_user_language_selected: 'common/id_user_language_selected',
			appointment_types: 'registration_lists/appointment_types',
			languages: 'params/languages',
			num_rows: 'datatable/num_rows',
			presential_columns: 'datatable_columns/presential_columns',
			tags: 'registration_lists/tags',
			reports: 'registration_lists/reports',
			id_user_client_selected: 'common/id_user_client_selected',
			user_client_module_items: 'common/user_client_module_items',
			user_module_items: 'common/user_module_items',
			reports_store: 'registration_lists/reports',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		getOptionsFilter() {
			return {
				appointment_types: this.appointment_types ? this.appointment_types : [],
				statuses: [
					{ value: 1, label: this.$t('pre_scheduled') },
					{ value: 2, label: this.$t('scheduled') },
					{ value: 3, label: this.$t('in_progress') },
					{ value: 4, label: this.$t('intermission') },
					{ value: 5, label: this.$t('awaiting_results') },
					{ value: 6, label: this.$t('reproved') },
					{ value: 7, label: this.$t('canceled') },
					{ value: 8, label: this.$t('finished') },
					{ value: 9, label: this.$t('model') },
				],
			}
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},

		filteredReports() {
			let obj = this.reports.filter((obj) => {
				return (
					obj.slug_type === 'presential_trainings' &&
					obj.url != 'https://www.take5.com.br' &&
					obj.url != ''
				)
			})
			return obj
		},
	},

	methods: {
		fetchLists() {
			this.$axios
				.get('/accounts/get_manager_registration_lists/', this.getHeader)
				.then((response) => {
					this.$store.dispatch('registration_lists/SET_LISTS_PRESENTIAL', [response.data])
				})
				.catch((e) => {
					console.log(e)
				})
		},

		requestAppointments({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			const order = pagination.descending ? '-' : ''
			const order_by = pagination.sortBy ? order + pagination.sortBy : ''

			const data = {
				selected_date: this.result_date,
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				user_requester__id__in: this.filter_user_requester.join(','),
				instructors__id__in: this.filter_user_instructor.join(','),
				company_branch__id__in: this.filter_company_branch.join(','),
				presential_event_appointment__appointment_type__id__in: this.filter_appointment_type.join(
					','
				),
				status__in: this.filter_status.join(','),
				created_date__gte: this.filter_start_date,
				created_date__lte: this.filter_end_date,
				presential_event_dates__hr_start__gte: this.filter_event_start_date,
				presential_event_dates__hr_end__lte: this.filter_event_end_date,
				ordering: order_by,
			}

			this.savePagination()

			this.$axios
				.get(
					`/presential/presential_events/list_presential_events/?${this.$qs.stringify(
						data,
						{
							encode: false,
							indices: false,
							arrayFormat: 'comma',
						}
					)}`,
					this.getHeader
				)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count

					this.appointments = response.data.results.map((r) => {
						return {
							...r,
							instructors_list: r.instructors_list
								? r.instructors_list.map((e) => e.name)
								: [],
							created_date: date.formatDate(r.created_date, 'DD/MM/YYYY HH:mm'),
							updated_date: date.formatDate(r.updated_date, 'DD/MM/YYYY HH:mm'),
							qty_vacancies: r.qty_vacancies,
							first_event_date: r.first_event_date
								? this.fEventDate(r.first_event_date.hr_start, r.timezone)
								: '',
							last_event_date: r.last_event_date
								? this.fEventDate(r.last_event_date.hr_end, r.timezone)
								: '',
							tags: r.tags ? r.tags.map((t) => t.id) : [],
							actions: r.id,
						}
					})
					if (
						!this.permissions.view_pre_scheduled_training &&
						this.appointments &&
						this.appointments.length
					) {
						this.appointments = this.appointments.filter((a) => a.status !== 1)
					}
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.loading = false
				})
		},

		clearAndRequest() {
			this.filter_user_requester = []
			this.filter_user_instructor = []
			this.filter_company_branch = []
			this.filter_appointment_type = []
			this.filter_status = []
			this.filter_start_date = []
			this.filter_end_date = []
			this.filter_event_start_date = []
			this.filter_event_end_date = []

			this.requestAppointments({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: this.num_rows,
					descending: false,
					sortBy: null,
				},
			})
		},

		requestFiltered({
			user_requester,
			user_instructor,
			company_branch,
			appointment_type,
			status,
			start_date,
			end_date,
			event_start_date,
			event_end_date,
		}) {
			this.filter_user_requester = user_requester
			this.filter_user_instructor = user_instructor
			this.filter_company_branch = company_branch
			this.filter_appointment_type = appointment_type
			this.filter_status = status
			this.filter_start_date = start_date
			this.filter_end_date = end_date
			this.filter_event_start_date = event_start_date
			this.filter_event_end_date = event_end_date

			this.requestAppointments({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: this.num_rows,
					descending: false,
					sortBy: null,
				},
			})
		},

		showPresentialReport(presential) {
			this.action_id = presential.id
			this.action_status = presential.status
			this.action_title = presential.title
			this.show_report_presential = true
		},

		downloadReport() {
			if (this.$buttonDelay('appointments_report_button_delay')) return
			let message = this.$t('generating_import_file_message')
			this.$q.loading.show({
				message,
			})
			this.$axios
				.get(`/presential/presential_events/generate_report/`, this.getHeader)
				.then((response) => {
					this.forceFileDownload(response.data.path_url)
				})
				.catch((e) => {
					this.loading = false
					this.show_report_problem = true
					console.log(e)
				})
				.finally(() => {
					this.$store.dispatch('common/SET_APPOINTMENTS_REPORT_BUTTON_DELAY', [
						new Date(),
					])
					this.$q.loading.hide()
				})
		},

		forceFileDownload(response) {
			const link = document.createElement('a')
			link.href = response
			document.body.appendChild(link)
			link.click()
		},

		checkPermissions() {
			const client = this.user_client_module_items.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (client) {
				for (const [key, value] of Object.entries(this.permissions)) {
					let obj = client.module_items.find((obj) => obj.slug === key)
					if (obj) {
						this.permissions[key] = obj.permission
					}
				}
			} else return
		},

		showColumn(list, name) {
			if (list[name]) return list[name].show
			else return false
		},

		saveColumns() {
			this.show_customize_columns = false
			this.saveDatatableColumns()
		},

		getStatus(id) {
			switch (id) {
				case 1:
					return 'pre_scheduled'
				case 2:
					return 'scheduled'
				case 3:
					return 'in_progress'
				case 4:
					return 'intermission'
				case 5:
					return 'awaiting_results'
				case 6:
					return 'reproved'
				case 7:
					return 'canceled'
				case 8:
					return 'finished'
				case 9:
					return 'model'
			}
		},

		getStatusColor(id) {
			switch (id) {
				case 1:
					return '#ffeb3b'
				case 2:
					return '#ba68c8'
				case 3:
					return '#3f51b5'
				case 4:
					return '#757575'
				case 5:
					return '#f57f17'
				case 6:
					return '#424242'
				case 7:
					return '#e53935'
				case 8:
					return '#43a047'
				case 9:
					return '#607d8b'
			}
		},

		getStatusColorChip(id) {
			switch (id) {
				case 1:
					return 'light-blue-5'
				case 2:
					return 'purple-4'
				case 3:
					return 'indigo'
				case 4:
					return 'grey-7'
				case 5:
					return 'yellow-10'
				case 6:
					return 'dark'
				case 7:
					return 'red-7'
				case 8:
					return 'green-7'
				case 9:
					return 'blue-grey-6'
			}
		},

		getStatusIcon(id) {
			switch (id) {
				case 1:
					return 'fas fa-history'
				case 2:
					return 'far fa-clock'
				case 3:
					return 'fas fa-play'
				case 4:
					return 'fas fa-pause'
				case 5:
					return 'far fa-question-circle'
				case 6:
					return 'far fa-times-circle'
				case 7:
					return 'fas fa-ban'
				case 8:
					return 'fas fa-check'
				case 9:
					return 'far fa-id-card'
			}
		},

		showReport(report) {
			this.report_url = report.url
			this.dropdown_reports = false
			this.show_report = true
		},

		showBillingReports(report) {
			this.show_billing_reports_modal = true
		},

		hideBillingReports() {
			this.show_billing_reports_modal = false
		},

		hideReport() {
			this.action_id = null
			this.action_status = null
			this.action_title = null
			this.show_report_presential = false
		},

		getQuantityTags(tags) {
			return tags.length > 0 ? tags.length : '0'
		},

		getTag(tag) {
			let obj = this.tags.find((obj) => obj.value === tag)
			return obj ? obj.label : ''
		},

		fEventDate(date, timezone) {
			return date && timezone
				? moment(date)
						.tz(timezone)
						.format(
							this.id_user_language_selected == 1 ||
								this.id_user_language_selected == 3
								? 'DD/MM/YYYY HH:mm'
								: 'MM-DD-YYYY HH:mm'
						)
				: date.formatDate(date, 'DD/MM/YYYY HH:mm')
		},

		fDate(d) {
			if (d) {
				let format = ''

				switch (this.id_user_language_selected) {
					case 1:
					case 3:
						format = 'DD/MM/YYYY'
						break
					default:
						format = 'MM-DD-YYYY'
						break
				}

				return moment(d)
					.tz('Etc/GMT')
					.format(format)
			}
		},

		fHour(d) {
			if (d) {
				return date.formatDate(d, 'HH:mm:ss')
			}
		},

		getAppointmentType(id) {
			if (this.appointment_types) {
				let obj = this.appointment_types.find((obj) => {
					return obj.value === id
				})
				if (obj) return obj.label
			} else return ''
		},

		showGuests(event) {
			if (
				event.status === 1 ||
				event.status === 2 ||
				event.status === 4 ||
				event.status === 6 ||
				event.status === 9
			)
				return false
			else return true
		},

		savePagination() {
			this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
		},

		loadPagination() {
			this.server_pagination.rowsPerPage = this.num_rows
		},

		saveDatatableColumns() {
			this.$store.dispatch('datatable_columns/SET_PRESENTIAL_COLUMNS', [this.columns])
		},

		loadDatatableColumns() {
			let item = this.user_module_items.find(
				(e) => e.slug === 'edit_presential_manager_seats'
			)

			if (!item) {
				this.columns = this.columns.filter((c) => c.name !== 'qty_vacancies')

				const storedCol = this.presential_columns.find((c) => c.name === 'qty_vacancies')
				if (storedCol) {
					this.$store.dispatch('datatable_columns/SET_PRESENTIAL_COLUMNS', [this.columns])
				}
				if (
					process.env.PROJECT_FOLDER !== 'universidadechevrolet' &&
					process.env.PROJECT_FOLDER !== 'academiacoral'
				) {
					this.columns = this.columns.filter((c) => c.name !== 'code')

					const storedCol = this.presential_columns.find((c) => c.name === 'code')
					if (storedCol) {
						this.$store.dispatch('datatable_columns/SET_PRESENTIAL_COLUMNS', [
							this.columns,
						])
					}
				}
			}
			if (this.$compareStoredColumns(this.columns, this.presential_columns))
				this.columns = this.presential_columns
			else return
		},

		loadReports() {
			if (this.reports_store)
				this.report_list = this.reports_store.filter((obj) => {
					return (
						obj.slug_type === 'presential_trainings' &&
						obj.url != 'https://www.take5.com.br' &&
						obj.url != ''
					)
				})
		},

		getAuthParamsData(url_original) {
			this.$q.loading.show()
			this.$axios
				.get('/reports/reports/get_looker_studio_challenge_token/', this.getHeader)
				.then((response) => {
					this.report_url = `${url_original}?${response.data.params}`
					this.show_report = true
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		showReportBI(report) {
			this.$refs.dropdownReport.hide()
			if (report.report_type === 3) {
				this.getAuthParamsData(report.url_original)
			} else {
				this.report_url = report.url
				this.show_report = true
			}
		},
	},

	created() {
		this.loadReports()
		this.checkPermissions()
		this.loadDatatableColumns()
		this.loadPagination()
		this.fetchLists()
		if (!this.permissions.view_pre_scheduled_training) {
			this.filter_status = !this.permissions.manage_models
				? [2, 3, 4, 5, 6, 7, 8]
				: [2, 3, 4, 5, 6, 7, 8, 9]
			this.requestAppointments({ pagination: this.server_pagination })
		} else {
			this.requestAppointments({ pagination: this.server_pagination })
		}
	},
}
</script>

<style lang="stylus" scoped>
.filter-card
    border-radius 4px
    box-shadow 0 1px 2px rgba(0,0,0,0.2), 0 1px 1px rgba(0,0,0,0.14), 0 2px 1px -1px rgba(0,0,0,0.12)
</style>
